import { Box, Paper } from "@mui/material";
import { useState } from "react";
import { useParams } from "react-router";
import { Typography } from "@platform/design-system";

import { CircularProgressCentered } from "../../../../components/CircularProgressCentered";
import { useRfpClientDetails } from "../../../../hooks/useRfpClientDetails";
import { useRfpDetails } from "../../../../hooks/useRfpDetails";

import { CoverageDecisionRecord } from "./types";
import { FinalDecisionsDrawer } from "./FinalDecisionsDrawer";
import { FinalDecisionsHeader } from "./FinalDecisionsHeader";
import { FinalDecisionsReviews } from "./FinalDecisionsReviews";
import { getFinalDecisionConfig } from "./getFinalDecisionConfig";
import { PostSubmitNotifications } from "./PostSubmitNotifications";
import {
  useFinalDecisionQuery,
  useInitializeFinalDecision,
} from "./useFinalDecisionApi";
import { usePostSubmitNotifications } from "./usePostSubmitNotifications";

export const FinalDecisions = (): React.JSX.Element => {
  const { id: rfpId } = useParams<{ id: string }>();
  const { data: rfpDetails, isLoading } = useRfpDetails({ rfpId });
  const { data: clientDetails } = useRfpClientDetails({ rfpId });
  const { data: finalDecisionData } = useFinalDecisionQuery({ rfpId });
  const upsertFinalDecision = useInitializeFinalDecision();
  const { handlePostSubmit, showMetlifeModal, hideMetlifeModal } =
    usePostSubmitNotifications({ rfpId });

  const [showDrawer, setShowDrawer] = useState(false);

  if (isLoading) {
    return <CircularProgressCentered />;
  }

  const handleFinalDecisionClick = (): void => {
    upsertFinalDecision.mutate(rfpId, {
      onSuccess: () => {
        setShowDrawer(true);
      },
    });
  };

  const { message, showReview, buttonConfig } = getFinalDecisionConfig({
    rfpStatus: rfpDetails?.status || null,
    hasFormDraft: !!finalDecisionData?.draftId,
    onFinalDecisionClick: handleFinalDecisionClick,
    hasFinalDecisions: !!finalDecisionData?.coverageDecisions?.length,
  });

  const handleClose = (): void => {
    setShowDrawer(false);
  };

  const handleSubmitSuccess = (
    coverageDecisions: CoverageDecisionRecord[],
  ): void => {
    setShowDrawer(false);
    handlePostSubmit(coverageDecisions);
  };

  return (
    <Box>
      <FinalDecisionsHeader
        buttonConfig={buttonConfig}
        isFinalDecisionLoading={upsertFinalDecision.isPending}
        onFinalDecisionClick={handleFinalDecisionClick}
      />
      {message && (
        <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
          <Typography>{message}</Typography>
        </Paper>
      )}
      {showReview && (
        <FinalDecisionsReviews
          rfpId={rfpId}
          data={finalDecisionData.submittedFormValues}
        />
      )}

      {showDrawer && (
        <FinalDecisionsDrawer
          clientDetails={clientDetails}
          onClose={handleClose}
          onSubmitSuccess={handleSubmitSuccess}
        />
      )}

      <PostSubmitNotifications
        showMetlifeModal={showMetlifeModal}
        onClose={hideMetlifeModal}
      />
    </Box>
  );
};
